/**
 * THE PROJECT GUIDE — the one derivation two surfaces share (#4 continued).
 *
 * WHY THIS MODULE EXISTS. The lifecycle guide is drawn by the full "Set up & run
 * this project" panel on the Plan (ProjectGuide.tsx). The truth about every stage
 * is worked out in exactly one place — here — so the panel never has to decide a
 * stage's state for itself. (A condensed copy once sat at the top of the Board;
 * on 24 Sep 2026 the board-metrics strip took that slot, and the Plan panel is
 * now the guide's only surface.)
 *
 * IT IS DERIVED, NOT TRACKED. Each stage's state comes from the same data the
 * rest of the app shows — the documents, the About fields, the backlog, the
 * phases, the sprints, the releases — so a step goes green the moment the real
 * work is done, with nothing to tick and nothing that can drift from the truth.
 *
 * NAVIGATION IS DESCRIBED, NOT BOUND. Every stage carries a `target` — where to
 * go to work on it — as plain data, not a bound closure, so each surface can
 * decide how to present it (the Plan guide shows a CTA on some, a status on
 * others; the Board strip makes every step a clickable target). `runStageTarget`
 * turns a target into the actual navigation.
 */
import type { Project } from './projects';
import type { Doc } from './types';
import type { Phase } from './phases';
import type { Sprint } from './sprints';
import type { Build } from './types';
import type { View } from './store';
import { livePhases } from './phases';
import { richHasText } from './rich-text';

export type StageState = 'done' | 'now' | 'todo';

/** Where a stage sends you to work on it — plain data both surfaces can act on. */
export type NavTarget =
  | { kind: 'drill'; view: View; reveal?: string }
  | { kind: 'draft'; doc: 'charter' | 'requirements'; title: string };

export interface Stage {
  key: string;
  /** The full name, as the Plan guide shows it. */
  name: string;
  /** The one- or two-word name for the condensed Board strip. */
  short: string;
  state: StageState;
  what: string;
  /** A short "what this feeds / reads" chip, when the connection is worth naming. */
  feeds?: string;
  /** Where to go to work on this stage — always present; the Board strip uses it on every step. */
  target: NavTarget;
  /** When present, the Plan guide renders a clickable CTA with this label; otherwise it shows `status`. */
  actionLabel?: string;
  /** The plain status line the Plan guide shows when there is no `actionLabel`. */
  status?: string;
}

export interface StageInput {
  project: Project | undefined;
  documents: Doc[];
  phases: Phase[];
  issues: { moscow?: unknown }[];
  sprints: Sprint[];
  builds: Build[];
  /** Whether this viewer may set up the project (can(role, 'editSettings')). */
  mayPlan: boolean;
}

/**
 * The seven lifecycle stages, each with its derived state and where it sends
 * you. The single source of truth behind both the Plan guide and the Board strip.
 */
export function projectStages(input: StageInput): Stage[] {
  const { project, documents, phases, issues, sprints, builds, mayPlan } = input;

  const charterDoc = documents
    .filter((d) => d.kind === 'charter')
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0];
  const reqDoc = documents
    .filter((d) => d.kind === 'requirements')
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0];

  const scopeSet = richHasText(project?.scopeIn) || richHasText(project?.scopeOut);
  const peopleSet =
    !!project?.sponsor?.trim() || (project?.stakeholders?.some((s) => s.name?.trim()) ?? false);

  const totalCards = issues.length;
  const unweighed = issues.filter((i) => !i.moscow).length;

  const live = livePhases(phases);
  const running = sprints.filter((s) => s.state === 'active');

  const anySigned = documents.some((d) => d.state === 'signed');
  const anyAwaiting = documents.some((d) => d.state === 'awaiting');
  const readyToSend = documents.some((d) => d.state === 'draft' && !!d.filePath);

  // 1 · Charter
  const charter: Stage = charterDoc
    ? {
        key: 'charter',
        name: 'Charter',
        short: 'Charter',
        state: 'done',
        feeds: 'feeds Requirements',
        what: 'The mandate — sponsor, objective, scope in & out, stakeholders, assumptions.',
        target: { kind: 'drill', view: 'documents', reveal: charterDoc.id },
        actionLabel: 'Open the charter →',
      }
    : {
        key: 'charter',
        name: 'Charter',
        short: 'Charter',
        state: 'now',
        feeds: 'feeds Requirements',
        what: 'The mandate — sponsor, objective, scope in & out, stakeholders, assumptions.',
        ...(mayPlan
          ? {
              target: { kind: 'draft', doc: 'charter', title: 'Project charter' } as NavTarget,
              actionLabel: 'Start the charter →',
            }
          : { target: { kind: 'drill', view: 'documents' } as NavTarget, status: 'Not started yet' }),
      };

  // 2 · Scope & stakeholders — set in About now (#2), which the charter reads.
  const scopeDone = scopeSet && peopleSet;
  const scope: Stage = {
    key: 'scope',
    name: 'Scope & stakeholders',
    short: 'Scope',
    state: scopeDone ? 'done' : charterDoc || scopeSet || peopleSet ? 'now' : 'todo',
    what: "What's in and out of this release, and who signs it off.",
    target: { kind: 'drill', view: 'plan', reveal: 'about' },
    ...(scopeDone
      ? { status: 'Set in Project details' }
      : mayPlan
        ? { actionLabel: 'Set them in Project details ↓' }
        : { status: 'Not set yet' }),
  };

  // 3 · Requirements — generated live from the backlog.
  const reqState: StageState =
    totalCards === 0 ? 'todo' : reqDoc && unweighed === 0 ? 'done' : 'now';
  const requirements: Stage = {
    key: 'requirements',
    name: 'Requirements',
    short: 'Requirements',
    state: reqState,
    feeds: totalCards ? `← your ${totalCards} card${totalCards === 1 ? '' : 's'}` : undefined,
    what:
      totalCards === 0
        ? 'Built live from your backlog — add cards and they become the requirements.'
        : unweighed > 0
          ? `Built live from your backlog. ${unweighed} of ${totalCards} aren't weighed for the release yet — set “Release necessity” so the summary means something.`
          : 'Built live from your backlog — every requirement weighed for the release.',
    ...(reqDoc
      ? {
          target: { kind: 'drill', view: 'documents', reveal: reqDoc.id } as NavTarget,
          actionLabel: 'Open & weigh them →',
        }
      : mayPlan && totalCards > 0
        ? {
            target: { kind: 'draft', doc: 'requirements', title: 'Requirements' } as NavTarget,
            actionLabel: 'Start the requirements →',
          }
        : {
            target: {
              kind: 'drill',
              view: totalCards === 0 ? 'backlog' : 'documents',
            } as NavTarget,
            status: totalCards === 0 ? 'No cards yet' : 'Not started yet',
          }),
  };

  // 4 · Plan & phases — the Plan itself; the Board strip links there to add them.
  const phaseNames = live.map((p) => p.name).join(' · ');
  const planPhases: Stage = {
    key: 'phases',
    name: 'Plan & phases',
    short: 'Phases',
    state: live.length > 0 ? 'done' : 'now',
    what: 'The phases the work moves through, with a Go-Live date and a health verdict.',
    target: { kind: 'drill', view: 'plan' },
    status: live.length > 0 ? phaseNames : 'No phases yet',
  };

  // 5 · Sprints
  const sprintStage: Stage = {
    key: 'sprints',
    name: 'Sprints',
    short: 'Sprints',
    state: running.length > 0 ? 'now' : sprints.length > 0 ? 'done' : 'todo',
    what: 'Timebox the work. As a card enters a sprint, weigh it for the release.',
    target: { kind: 'drill', view: 'sprints' },
    status:
      running.length > 0
        ? `${running.length} running`
        : sprints.length > 0
          ? `${sprints.length} sprint${sprints.length === 1 ? '' : 's'}`
          : 'None yet',
  };

  // 6 · Sign-off
  const signoff: Stage = {
    key: 'signoff',
    name: 'Sign-off',
    short: 'Sign-off',
    state: anySigned ? 'done' : readyToSend || anyAwaiting ? 'now' : 'todo',
    feeds: 'from Requirements & Change requests',
    what: 'Produce a document as a PDF and route it to the people who approve it.',
    target: { kind: 'drill', view: 'documents' },
    actionLabel: 'Start a sign-off →',
  };

  // 7 · Release
  const release: Stage = {
    key: 'release',
    name: 'Release',
    short: 'Release',
    state: builds.length > 0 ? 'done' : 'todo',
    what: 'Cut a build with release notes when the work is signed off and done.',
    target: { kind: 'drill', view: 'releases' },
    actionLabel: 'Go to releases →',
  };

  return [charter, scope, requirements, planPhases, sprintStage, signoff, release];
}

/**
 * Turn a stage's `target` into actual navigation. `draft` targets create the
 * document and open it, surfacing any problem through `onProblem`; `drill`
 * targets jump straight to the view.
 */
export function runStageTarget(
  target: NavTarget,
  drillTo: (view: View, reveal?: string) => void,
  draftDocument: (kind: 'charter' | 'requirements', title: string) => Promise<{ id: string; problem: string }>,
  onProblem?: (message: string) => void,
): void {
  if (target.kind === 'drill') {
    drillTo(target.view, target.reveal);
    return;
  }
  onProblem?.('');
  void draftDocument(target.doc, target.title).then(({ id, problem }) => {
    if (problem) {
      onProblem?.(problem);
      return;
    }
    drillTo('documents', id);
  });
}
